import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { MainHead } from '../../../components/common/head/MainHead';
import { Icon } from '../../../components/common/icon/Icon';
import { useLayout } from '../../../hooks/screen/useLayout';

import { SlideModal } from '../../../components/abstract/modal/slide/SlideModal';
import { SlideModalSmoother } from '../../../components/abstract/modal/slide/SlideModalSmoother';
import { ImageCounterRightBottom } from '../../../components/common/counter/ImageCounterRightBottom';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { MachineSpecTable } from '../../../components/common/machine/MachineSpecTable';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import { CopyCompleteModal } from '../../../components/custom/modal/toast/CopyCompleteModal';
import { usePostDictionaryMachineReportsMutation } from '../../../hooks/api/dictionary/mutations/usePostDictionaryMachineReports.mutation';
import { useGetDictionaryUnitMapQuery } from '../../../hooks/api/dictionary/queries/useGetDictionaryUnitMap.query';
import { useGetDictionaryWordMapQuery } from '../../../hooks/api/dictionary/queries/useGetDictionaryWordMap.query';
import { useGetMachineByIdQuery } from '../../../hooks/api/machines/queries/useGetMachineById.query';
import { postMessageToRN } from '../../../utils/RNCommunicator/postMessageToRN.util';

export const DictionaryMachineDetailPage = () => {
  const { machineIdx } = useParams();
  const [selectedTab, setSelectedTab] = useState<string>('요약');
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isMenuModalOpen, setIsMenuModalOpen] = useState<boolean>(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
  const [isReportCompleteModalOpen, setIsReportCompleteModalOpen] = useState<boolean>(false);
  const [isCopyCompleteModalOpen, setIsCopyCompleteModalOpen] = useState<boolean>(false);
  const [reportText, setReportText] = useState<string>('');

  const { mutate: postDictionaryMachineReports } = usePostDictionaryMachineReportsMutation();

  const { data: dictionaryMachine, isLoading: isGetDictionaryMachineQueryLoading } = useGetMachineByIdQuery({
    machineId: machineIdx ?? '',
  });

  const { data: unitMap, isLoading: isUnitMapQueryLoading } = useGetDictionaryUnitMapQuery();
  const { data: wordMap, isLoading: isWordMapQueryLoading } = useGetDictionaryWordMapQuery();

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: true }} />,
    fixedHeaderHeight: 44,
    fixedFooterHeight: 92,
  });

  if (isGetDictionaryMachineQueryLoading || isUnitMapQueryLoading || isWordMapQueryLoading) return <AnimateLoading />;

  return (
    <>
      <Container>
        <HeaderSection>
          <MachineImage src={''} />
          <MachineInfo>
            {dictionaryMachine && (
              <>
                <MachineBrand>{dictionaryMachine.brand?.name}</MachineBrand>
                <MachineSeries>{dictionaryMachine.model}</MachineSeries>
                <MachineModel>{dictionaryMachine.model}</MachineModel>
              </>
            )}
          </MachineInfo>
          <HeaderOption onClick={() => setIsMenuModalOpen((prev) => !prev)}>
            <Icon name={'MeatBallButton'} width={20} height={20} />
          </HeaderOption>
        </HeaderSection>

        <TabSection>
          <Tab isSelectedTab={selectedTab === '요약'} onClick={() => setSelectedTab('요약')}>
            {'요약'}
          </Tab>
          <Tab isSelectedTab={selectedTab === '제원'} onClick={() => setSelectedTab('제원')}>
            {'제원'}
          </Tab>
        </TabSection>

        <TabContentWrapper isShow={selectedTab === '요약'}>
          <ImageSliderSection
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Swiper
              navigation
              modules={[Navigation]}
              onSlideChange={(data) => {
                setActiveIndex(data.realIndex);
              }}
            >
              {dictionaryMachine?.images?.map((imageURL) => (
                <CustomSwiperSlide>
                  <MachineSlideImage src={imageURL} alt={imageURL} />
                  <div className={'swiper-lazy-preloader'} />
                </CustomSwiperSlide>
              ))}
            </Swiper>
            {dictionaryMachine?.images && dictionaryMachine?.images.length > 0 && (
              <ImageCounterRightBottom
                currentPageNum={activeIndex + 1}
                totalPageNum={dictionaryMachine?.images?.length ?? 0}
              />
            )}
          </ImageSliderSection>

          {dictionaryMachine && wordMap && unitMap && (
            <MachineSpecTable machineData={dictionaryMachine} wordMap={wordMap} unitMap={unitMap} type={'short'} />
          )}
        </TabContentWrapper>

        <TabContentWrapper isShow={selectedTab === '제원'}>
          {dictionaryMachine && wordMap && unitMap && (
            <MachineSpecTable machineData={dictionaryMachine} wordMap={wordMap} unitMap={unitMap} type={'long'} />
          )}
        </TabContentWrapper>
      </Container>

      <SlideModalSmoother
        targetState={isMenuModalOpen}
        openDelayTime={0}
        handleCloseButtonClick={() => setIsMenuModalOpen(false)}
        height={'300px'}
      >
        <SlideModalHeaderSection>
          <SlideModalHeaderContent>{'추가 기능'}</SlideModalHeaderContent>
        </SlideModalHeaderSection>
        <SlideModalBodySection>
          <SlideModalBodyContent
            onClick={() => {
              if (window.ReactNativeWebView) {
                postMessageToRN({ messageKey: 'share', messageParam: window.location.href });
              } else {
                window.navigator.clipboard.writeText(window.location.href);
                setIsCopyCompleteModalOpen(true);
                setIsMenuModalOpen(false);
              }
            }}
          >
            {'공유'}
          </SlideModalBodyContent>
          <SlideModalBodyContent
            color={'#FF3049'}
            onClick={() => {
              setIsMenuModalOpen(false);
              setIsReportModalOpen(true);
            }}
          >
            {'잘못된 정보 신고'}
          </SlideModalBodyContent>
        </SlideModalBodySection>
        <SlideModalBottomSection>
          <SlideModalBottomContent onClick={() => setIsMenuModalOpen(false)}>{'닫기'}</SlideModalBottomContent>
        </SlideModalBottomSection>
      </SlideModalSmoother>

      <SlideModal
        isSlideModalOpen={isReportModalOpen}
        closeAll={() => setIsReportModalOpen(false)}
        showSlideButton={false}
      >
        <ReportForm>
          <ReportFormTitle>{'잘못된 정보 신고'}</ReportFormTitle>
          <ReportFormSubTitle>{'내용을 적어주시면 확인후\n빠른 시일내에 시정하겠습니다.'}</ReportFormSubTitle>
          <ReportFormTextarea
            value={reportText}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReportText(e.target.value)}
            placeholder={'문의하실 내용을 적어주세요.'}
          />
        </ReportForm>
        <SubmitButton
          type={'submit'}
          onClick={() => {
            postDictionaryMachineReports(
              {
                machineId: machineIdx!,
                reqBody: { detail: reportText },
              },
              {
                onSuccess: () => {
                  setIsReportModalOpen(false);
                  setIsReportCompleteModalOpen(true);
                  setReportText('');
                },
              },
            );
          }}
        >
          {'오류 제보'}
        </SubmitButton>
        <CloseButton type={'button'} onClick={() => setIsReportModalOpen(false)}>
          {'닫기'}
        </CloseButton>
      </SlideModal>

      <PopupConfirmModal
        isShow={isReportCompleteModalOpen}
        closeModal={() => setIsReportCompleteModalOpen(false)}
        description={'접수가 완료 되었어요.'}
        buttonArray={[
          {
            text: '확인',
            onClick: () => setIsReportCompleteModalOpen(false),
            color: 'blue',
          },
        ]}
      />

      <CopyCompleteModal isShowModal={isCopyCompleteModalOpen} onClose={() => setIsCopyCompleteModalOpen(false)} />
    </>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  padding: 16px;
  gap: 16px;
`;

const HeaderSection = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;

  gap: 16px;
`;

const MachineImage = styled.img`
  width: 64px;
  height: 64px;

  border-radius: 8px;
  background-color: #ff3049;
`;

const MachineInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  flex-grow: 1;

  gap: 2px;
`;

const MachineBrand = styled.span`
  color: #505363;
  font-size: 12px;
  line-height: 100%; /* 12px */
`;

const MachineSeries = styled.span`
  color: #232429;
  font-size: 18px;
  font-weight: 700;
  line-height: 100%; /* 18px */
`;

const MachineModel = styled.span`
  color: #505363;
  line-height: 100%; /* 16px */
`;

const HeaderOption = styled.button``;

const TabSection = styled.div`
  width: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 12px 0px;
`;

const Tab = styled.button<{ isSelectedTab: boolean }>`
  width: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 8px 16px;

  color: ${(props) => (props.isSelectedTab ? '#232429' : '#AEAFB6')};
  text-align: center;
  font-size: 14px;
  line-height: 100%; /* 14px */

  border-bottom: 1px solid ${(props) => (props.isSelectedTab ? '#232429' : '#AEAFB6')};
`;

const ImageSliderSection = styled.div`
  width: 100%;
  height: 100%;

  position: relative;
`;

const CustomSwiperSlide = styled(SwiperSlide)`
  width: 100%;
  position: relative;

  padding-top: 75%;

  cursor: pointer;
`;

const MachineSlideImage = styled.img`
  width: 100%;
  height: 100%;

  position: absolute;

  left: 0;
  top: 0;

  object-fit: cover;

  cursor: pointer;
`;

const TabContentWrapper = styled.div<{ isShow: boolean }>`
  width: 100%;
  height: 100%;

  display: ${(props) => (props.isShow ? 'block' : 'none')};
`;

const SlideModalHeaderSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;

  border-bottom: 1px solid #dcdde0;
`;

const SlideModalHeaderContent = styled.span`
  display: flex;
  padding: 8px 0px;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  color: #232429;
`;

const SlideModalBodySection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;

  gap: 10px;
`;

const SlideModalBodyContent = styled.div<{ color?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;

  color: ${(props) => (props.color ? props.color : '#232429')};

  cursor: pointer;
`;

const SlideModalBottomSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  gap: 10px;
`;

const SlideModalBottomContent = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  gap: 4px;

  color: #898b94;
`;

const ReportForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  margin-top: 8px;
`;

const ReportFormTitle = styled.span`
  color: #232429;
  font-size: 18px;
  font-weight: 700;
  line-height: 85%; /* 15.3px */

  margin-bottom: 8px;
`;

const ReportFormSubTitle = styled.span`
  color: #505363;
  text-align: center;
  font-size: 12px;
  line-height: 130%; /* 15.6px */

  margin-bottom: 24px;
`;

const ReportFormTextarea = styled.textarea`
  width: 100%;

  height: 80px;
  max-height: 100px;

  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  align-self: stretch;

  padding: 16px;

  gap: 8px;

  border-radius: 4px;
  border: 1px solid #efeff1;
  background: #fff;

  font-size: 12px;
  line-height: 130%; /* 15.6px */

  resize: none;

  ::placeholder {
    font-size: 12px;
  }
`;

const Button = styled.button`
  width: 100%;
  height: 46px;

  border-radius: 4px;

  font-size: 14px;
  line-height: 14px;
  font-weight: 500;

  cursor: pointer;
`;

const SubmitButton = styled(Button)`
  color: #fff;
  background-color: #446dff;

  margin-top: 24px;
`;

const CloseButton = styled(Button)`
  color: #898b94;

  margin-top: 8px;
`;
